'use client';
import React, { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const practices = [
  {
    id: 'regulatory',
    label: 'Regulatory Examinations',
    icon: 'ShieldCheckIcon' as const,
    headline: 'In the room before the examiner arrives.',
    body:
      'We prepare financial institutions for OCC, FDIC, Federal Reserve, and state banking department examinations. Our pre-exam readiness reviews have eliminated consent order risk for 34 consecutive clients.',
    stat: '34',
    statLabel: 'Exam matters, zero consent orders',
    span: 'lg:col-span-2 lg:row-span-2',
    dark: true,
  },
  {
    id: 'mergers',
    label: 'M&A & Merger Opinions',
    icon: 'DocumentCheckIcon' as const,
    headline: 'Opinions the Fed doesn\'t question.',
    body:
      'Bank merger approvals, holding company formations, and acquisition financing opinions. $14.2B in closed transactions across community, regional, and super-regional institutions.',
    stat: '$14.2B',
    statLabel: 'In closed transactions since 2016',
    span: 'lg:col-span-1',
    dark: false,
  },
  {
    id: 'fintech',
    label: 'Fintech Licensing',
    icon: 'CpuChipIcon' as const,
    headline: 'All 50 states. One engagement.',
    body:
      'Multi-state money transmitter, lending, and payments licensing. We closed twelve simultaneous state licenses in ninety days for a Series B payments company.',
    stat: '50',
    statLabel: 'State licensing coverage',
    span: 'lg:col-span-1',
    dark: false,
  },
  {
    id: 'compliance',
    label: 'Compliance Frameworks',
    icon: 'ClipboardDocumentCheckIcon' as const,
    headline: 'BSA/AML. CRA. Fair Lending. Built to hold.',
    body:
      'We design compliance programs that survive examiner scrutiny — BSA/AML policies, CRA strategic plans, fair lending frameworks, and UDAAP governance structures.',
    stat: '100%',
    statLabel: 'Client exam pass rate (2021–2025)',
    span: 'lg:col-span-2',
    dark: true,
  },
];

export default function PracticeAreasSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal-up, .reveal-stagger').forEach((el) =>
      observer.observe(el)
    );
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="practice-areas"
      ref={sectionRef}
      className="py-24 px-6 noise-overlay relative"
      style={{ background: 'var(--navy)' }}
    >
      {/* Decorative gold gradient top */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #B8943E, transparent)' }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="reveal-up mb-2">
          <span className="text-[10px] font-semibold tracking-widest3 uppercase text-gold/60">
            Practice Areas
          </span>
        </div>
        <div className="reveal-up mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h2
            className="font-serif font-light text-marble"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.2rem)', letterSpacing: '-0.02em' }}
          >
            Where We Work.
          </h2>
          <p className="text-[12px] text-marble/40 max-w-xs tracking-wide leading-relaxed md:text-right">
            Statute does not practice general corporate law. Every partner, every matter, every day — banking and finance regulation only.
          </p>
        </div>

        {/* Bento grid */}
        <div className="reveal-stagger grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
          {practices.map((p) => (
            <div
              key={p.id}
              className={`bento-card rounded-sm p-8 flex flex-col justify-between min-h-[280px] ${p.span}`}
              style={{
                background: p.dark
                  ? 'linear-gradient(135deg, #122444 0%, #0E2347 100%)'
                  : 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(184,148,62,0.15)',
              }}
            >
              {/* Top */}
              <div>
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-10 h-10 rounded-sm flex items-center justify-center"
                    style={{ background: 'rgba(184,148,62,0.12)', border: '1px solid rgba(184,148,62,0.25)' }}
                  >
                    <Icon name={p.icon} variant="outline" size={20} className="text-gold" />
                  </div>
                  <span className="text-[9px] font-semibold tracking-widest2 uppercase text-gold/40 pt-1">
                    {p.label}
                  </span>
                </div>
                <h3
                  className="font-serif font-light text-marble mb-3"
                  style={{ fontSize: 'clamp(1.1rem, 1.8vw, 1.4rem)', lineHeight: '1.25' }}
                >
                  {p.headline}
                </h3>
                <p className="text-[12px] text-marble/45 leading-relaxed">{p.body}</p>
              </div>

              {/* Bottom stat */}
              <div className="mt-6 pt-4" style={{ borderTop: '1px solid rgba(184,148,62,0.15)' }}>
                <p
                  className="font-serif text-gold"
                  style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', lineHeight: 1 }}
                >
                  {p.stat}
                </p>
                <p className="text-[10px] tracking-wider text-marble/35 uppercase mt-1">
                  {p.statLabel}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative gold gradient bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, #B8943E, transparent)' }}
      />
    </section>
  );
}